import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { emptyRegisterForm, type RegisterAccountType, type RegisterForm } from './register/types';
import RegisterStepAccountType from './register/RegisterStepAccountType';
import RegisterStepCreateAccount from './register/RegisterStepCreateAccount';
import RegisterStepVerifyEmail from './register/RegisterStepVerifyEmail';
import RegisterStepSuccess from './register/RegisterStepSuccess';
import { post } from '../shared/apiClient';

type Step = 1 | 2 | 3 | 4;

const RegisterPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<RegisterForm>(emptyRegisterForm);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateForm = useCallback((patch: Partial<RegisterForm>) => {
    setForm((f) => ({ ...f, ...patch }));
  }, []);

  const setAccountType = useCallback((v: RegisterAccountType) => {
    updateForm({ accountType: v });
  }, [updateForm]);

  const onContinueStep1 = useCallback(() => setStep(2), []);
  const onBackFrom2 = useCallback(() => setStep(1), []);

  const onNextFrom2 = useCallback(async () => {
    setRegisterError(null);
    setSubmitting(true);
    try {
      await post('/auth/register', {
        account_type: form.accountType,
        full_name: form.name,
        email: form.email,
        nif: form.nif,
        password: form.password,
        confirm_password    : form.confirmPassword,
        agree_terms: form.agreeTerms,
      });
      setStep(3);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : t('register.failed');
      setRegisterError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [form, t]);

  const onBackFrom3 = useCallback(() => setStep(2), []);
  const onBackFrom4 = useCallback(() => setStep(3), []);
  const onVerified = useCallback(() => setStep(4), []);
  const onResend = useCallback(() => {}, []); // handled inside RegisterStepVerifyEmail

  return (
    <AuthPageShell>
      {step === 1 ? (
        <RegisterStepAccountType
          value={form.accountType}
          onChange={setAccountType}
          onContinue={onContinueStep1}
        />
      ) : null}
      {step === 2 ? (
        <RegisterStepCreateAccount
          data={form}
          onChange={updateForm}
          onBack={onBackFrom2}
          onNext={onNextFrom2}
          error={registerError}
          submitting={submitting}
        />
      ) : null}
      {step === 3 ? (
        <RegisterStepVerifyEmail
          email={form.email}
          onBack={onBackFrom3}
          onVerified={onVerified}
          onResend={onResend}
        />
      ) : null}
      {step === 4 ? <RegisterStepSuccess onBack={onBackFrom4} /> : null}
    </AuthPageShell>
  );
};

export default RegisterPage;
